import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MemberService } from '../../../core/services/member.service';
import {
  TuiAlertService,
  TuiButtonModule,
  TuiErrorModule,
  TuiLoaderModule,
  TuiTextfieldControllerModule,
  TuiSvgModule,
} from '@taiga-ui/core';
import {
  TUI_DEFAULT_MATCHER,
  TuiBooleanHandler,
  TuiDay,
  tuiPure,
} from '@taiga-ui/cdk';
import { AsyncPipe, NgIf } from '@angular/common';
import {
  TuiDataListWrapperModule,
  TuiFieldErrorPipeModule,
  TuiInputDateModule,
  TuiSelectModule,
  TuiInputDateTimeModule,
  TuiInputModule,
  TuiInputNumberModule,
  TuiMultiSelectModule,
  TuiRadioLabeledModule,
  TuiTextareaModule,
  TUI_VALIDATION_ERRORS,
} from '@taiga-ui/kit';
import { AttendanceService } from '../../../core/services/attendance.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-add-attendance',
  standalone: true,
  imports: [
    AsyncPipe,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    TuiButtonModule,
    TuiDataListWrapperModule,
    TuiErrorModule,
    TuiFieldErrorPipeModule,
    TuiInputDateModule,
    TuiSelectModule,
    TuiInputModule,
    TuiInputNumberModule,
    TuiLoaderModule,
    TuiMultiSelectModule,
    TuiRadioLabeledModule,
    TuiTextareaModule,
    TuiTextfieldControllerModule,
    TuiInputDateTimeModule,
    TuiTextfieldControllerModule,
    TuiSvgModule,
  ],
  providers: [
    AttendanceService,
    MemberService,
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: {
        required: 'Required',
      },
    },
  ],
  templateUrl: './add-attendance.component.html',
  styleUrl: './add-attendance.component.scss',
})
export class AddAttendanceComponent implements OnInit {
  attendanceForm!: FormGroup;
  isLoading = false;
  members: any[] = [];
  search: string | null = '';
  currentDate!: TuiDay;
  sessions: readonly string[] = ['PT', 'Gym'];
  memberStringify = (item: string): string => item;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private attendanceService: AttendanceService,
    private memberService: MemberService,
    private readonly alerts: TuiAlertService
  ) {}

  @tuiPure
  filter(search: string | null): readonly string[] {
    return this.members
      .filter(
        (m: any) =>
          TUI_DEFAULT_MATCHER(m.firstname, search || '') ||
          TUI_DEFAULT_MATCHER(m.lastname, search || '') ||
          TUI_DEFAULT_MATCHER(m.membershipId, search || '')
      )
      .map((m: any) => `${m.membershipId} ${m.firstname} ${m.lastname}`);
  }

  tagValidator: TuiBooleanHandler<string> = () => true;

  ngOnInit(): void {
    this.initAttendanceForm();
    this.memberService.getPaidMembers().then((res) => {
      this.members = res || [];
      this.currentDate = this.getCurrentDate();
    });
  }

  private getCurrentDate(): TuiDay {
    const date = new Date();
    return new TuiDay(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private initAttendanceForm(): void {
    this.attendanceForm = this.fb.group({
      date: [this.getCurrentDate(), Validators.required],
      member: [[], Validators.required],
      session: [null, Validators.required],
    });
  }

  submit(): void {
    if (!this.attendanceForm.valid) {
      this.attendanceForm.markAllAsTouched();
      return;
    }

    const selectedMembers = this.attendanceForm.get('member')?.value || [];
    const selectedIds: string[] = [];

    selectedMembers.forEach((memberName: string) => {
      const id = memberName.split(' ')[0]; // membershipId
      const foundMember = this.members.find((m) => m.membershipId === id);
      if (foundMember) {
        selectedIds.push(foundMember._id);
      }
    });

    if (!selectedIds.length) {
      this.alerts.open('Please select at least one valid member', { status: 'error' }).subscribe();
      return;
    }

    this.attendanceForm.get('member')?.setValue(selectedIds);

    const payload = {
      ...this.attendanceForm.value,
      payment: { status: 'Paid' },
    };

    this.isLoading = true;
    this.attendanceService
      .createAttendance(payload)
      .then(() => {
        this.isLoading = false;
        this.alerts.open('Attendance recorded successfully').subscribe();
        this.navigateToView();
      })
      .catch(() => {
        this.isLoading = false;
        this.alerts.open('Error creating attendance record', { status: 'error' }).subscribe();
      });
  }

  cancel(): void {
    this.navigateToView();
  }

  navigateToView(): void {
    this.router.navigate(['admin/attendance']);
  }
}
